import { GaraLockup } from '@/client/components/brand/GaraLogo';
import { type Currency, currencySymbol, fmtAmount } from '@/client/lib/currency';
import { cn } from '@/client/lib/utils';

export type PreviewLine = {
  id: string;
  person_name: string;
  date: string;
  description: string;
  hours: number;
  rate: number;
};

interface InvoicePreviewProps {
  invoiceNumber: string;
  clientName: string;
  projectName: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  issueDate: string;
  dueDate: string;
  lines: PreviewLine[];
  rates: Record<string, number>;
  showNames: boolean;
  showDates?: boolean;
  vatPct: number;
  subtotal: number;
  vatAmount: number;
  total: number;
  notes: string;
  paymentTerms: string;
  lateFee: string;
  currency?: Currency;
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums font-medium">{value}</span>
    </div>
  );
}

function GaraLogoSmall() {
  return <GaraLockup size={20} className="text-foreground" />;
}

export function InvoicePreview({
  invoiceNumber,
  clientName,
  projectName,
  billingPeriodStart,
  billingPeriodEnd,
  issueDate,
  dueDate,
  lines,
  rates,
  showNames,
  showDates = false,
  vatPct,
  subtotal,
  vatAmount,
  total,
  notes,
  paymentTerms,
  lateFee,
  currency = 'EUR' as Currency,
}: InvoicePreviewProps) {
  // When showNames is off, aggregate lines with the same description
  const displayLines = showNames
    ? lines
    : (() => {
        const map = new Map<string, { description: string; hours: number; amount: number }>();
        for (const l of lines) {
          const cur = map.get(l.description) ?? { description: l.description, hours: 0, amount: 0 };
          cur.hours += l.hours;
          cur.amount += l.hours * (rates[l.person_name] ?? l.rate);
          map.set(l.description, cur);
        }
        return Array.from(map.values()).map((v) => ({
          id: v.description,
          person_name: '',
          date: '',
          description: v.description,
          hours: v.hours,
          rate: v.hours > 0 ? v.amount / v.hours : 0,
        }));
      })();

  const periodLabel =
    billingPeriodStart && billingPeriodEnd
      ? `${fmtDate(billingPeriodStart)}–${fmtDate(billingPeriodEnd)}`
      : '—';

  return (
    <div className="rounded-xl bg-card shadow-md p-8 text-[12px] leading-relaxed max-w-[420px] mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <GaraLogoSmall />
          <div className="mt-1.5 text-[10px] text-muted-foreground leading-relaxed">
            str. Alba-lulia 21, of.1
            <br />
            MD-2051 mun. Chisinau, Moldova
            <br />
            INDO 1026023033824
          </div>
        </div>
        <div className="text-right">
          <div className="font-bold text-xl tracking-tight">Invoice</div>
          <div className="font-mono text-[11px] text-muted-foreground mt-0.5">
            {invoiceNumber || 'INV-—'}
          </div>
        </div>
      </div>

      {/* Bill to + dates */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <div className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
            Bill to
          </div>
          <div className="font-semibold">{clientName || '—'}</div>
        </div>
        <div className="flex flex-col gap-1.5">
          <PreviewRow label="Project" value={projectName || '—'} />
          <PreviewRow label="Period" value={periodLabel} />
          <PreviewRow label="Issued" value={fmtDate(issueDate)} />
          <PreviewRow label="Due" value={fmtDate(dueDate)} />
        </div>
      </div>

      {/* Line items */}
      <div className="border-t border-b py-2.5 mb-3.5">
        <div className="grid grid-cols-[1fr_44px_52px_64px] gap-2 text-[9px] font-semibold uppercase tracking-widest text-muted-foreground py-1">
          <div>Description</div>
          <div className="text-right">Hours</div>
          <div className="text-right">Rate</div>
          <div className="text-right">Amount</div>
        </div>
        {displayLines.length === 0 ? (
          <div className="py-4 text-center text-muted-foreground text-[11px]">
            No lines selected
          </div>
        ) : (
          displayLines.map((l, i) => {
            const rate = showNames ? (rates[l.person_name] ?? l.rate) : l.rate;
            const amount = l.hours * rate;
            return (
              <div
                key={l.id}
                className="grid grid-cols-[1fr_44px_52px_64px] gap-2 py-2.5 border-t border-dashed text-[11px] items-baseline"
              >
                <div>
                  <div className="leading-snug">{l.description}</div>
                  {(() => {
                    const sub = [
                      showNames && l.person_name,
                      showDates && l.date ? l.date.split('-').slice(1).reverse().join('/') : false,
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return sub ? (
                      <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
                    ) : null;
                  })()}
                </div>
                <div className="text-right tabular-nums">{l.hours.toFixed(1)}h</div>
                <div className="text-right tabular-nums text-muted-foreground">
                  {currencySymbol(currency)}
                  {rate.toFixed(2)}
                </div>
                <div className="text-right tabular-nums font-semibold">
                  {fmtAmount(amount, currency)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Totals */}
      <div className="flex justify-end mb-6">
        <div className="w-[220px] flex flex-col gap-1.5">
          <PreviewRow
            label="Total hours"
            value={`${displayLines.reduce((s, l) => s + l.hours, 0).toFixed(1)} h`}
          />
          <PreviewRow label="Subtotal" value={fmtAmount(subtotal, currency)} />
          <PreviewRow label={`VAT (${vatPct}%)`} value={fmtAmount(vatAmount, currency)} />
          <div className="h-px bg-foreground/20 my-1" />
          <div className="flex justify-between items-baseline">
            <span className="text-[11px] font-semibold">Total due</span>
            <span className="tabular-nums font-bold text-[17px]">{fmtAmount(total, currency)}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className={cn(
          'text-[10px] text-muted-foreground leading-relaxed pt-3.5 border-t',
          !notes && !paymentTerms && 'opacity-50',
        )}
      >
        {notes ||
          [paymentTerms, lateFee ? `Late fee ${lateFee}.` : ''].filter(Boolean).join(' ') ||
          'No notes.'}
      </div>
    </div>
  );
}
